import './Hosts.css'; // Import the new CSS

import ImprovHost from './improvimania/HostScreen.js';
import GuessingHost from './guessing/HostScreen.js';
import TriviaHost from './trivia/HostScreen.js';
import LearnHost from './learn/HostScreen.js';
import AndSceneHost from './andscene/HostScreen.js';
import WordMonsterHost from './wordmonster/HostScreen.js';
import ReactionHost from './reaction/HostScreen.js';
import SetTheBarHost from './setthebar/HostScreen.js';

// Configuration for our custom game blocks
const GAME_LIBRARY = [
  {
    id: 'improv',
    category: 'Acting & Performance',
    title: 'Improvimania',
    desc: 'Act it out, adapt, and make everyone laugh!',
    icon: '🎭',
    gradient: 'linear-gradient(135deg, #ff0844 0%, #ffb199 100%)',
    shadow: 'rgba(255, 8, 68, 0.4)',
  },
  {
    id: 'andscene',
    category: 'Acting & Performance',
    title: 'And Scene',
    desc: 'Curate a the scene with a dramatic bang.',
    icon: '🎬',
    gradient: 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
    shadow: 'rgba(253, 160, 133, 0.4)',
  },
  {
    id: 'trivia',
    category: 'Brain & Trivia',
    title: 'Classic Trivia',
    desc: 'Test your knowledge against the room.',
    icon: '🧠',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    shadow: 'rgba(79, 172, 254, 0.4)',
  },
  {
    id: 'setthebar',
    category: 'Brain & Trivia',
    title: 'Set The Bar',
    desc: 'Pattern matching, IQ points, and bluff-vote refunds.',
    icon: '📈',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    shadow: 'rgba(102, 126, 234, 0.4)',
  },
  {
    id: 'guessing-gameshow',
    category: 'Brain & Trivia',
    title: 'Guessing Game Show',
    desc: 'Classic category board with host controls.',
    icon: '🤔',
    gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    shadow: 'rgba(67, 233, 123, 0.4)',
  },
  {
    id: 'guessing-spiral',
    category: 'Brain & Trivia',
    title: 'Guessing Spiral',
    desc: 'Race to center with spins, money, and questions.',
    icon: '🌀',
    gradient: 'linear-gradient(135deg, #00c9ff 0%, #92fe9d 100%)',
    shadow: 'rgba(0, 201, 255, 0.4)',
  },
  {
    id: 'learn',
    category: 'Brain & Trivia',
    title: 'Learning Lab',
    desc: 'Quiz your mind with wild facts.',
    icon: '📚',
    gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
    shadow: 'rgba(250, 112, 154, 0.4)',
  },
  {
    id: 'wordmonster',
    category: 'Action & Speed',
    title: 'Word Monsters',
    desc: 'Feed the beasts with the right words!',
    icon: '👾',
    gradient: 'linear-gradient(135deg, #c471ed 0%, #f64f59 100%)',
    shadow: 'rgba(196, 113, 237, 0.4)',
  },
  {
    id: 'reaction',
    category: 'Action & Speed',
    title: 'Reaction Rush',
    desc: "It's whackin' time! Whack-A-Mole with bombs.",
    icon: '⚡',
    gradient: 'linear-gradient(135deg, #b224ef 0%, #7579ff 100%)',
    shadow: 'rgba(117, 121, 255, 0.4)',
  },
];

const Hosts = ({
  socket, serverIP, sessionCreated, createSession, gameStarted,
  sessionId, players, setPlayers, rounds, setRounds, currentRound,
  sessionList, leaderboard, removePlayer, titleTheme, AudioPlayer,
  isEndScene, speakingTheme, guessingTheme, gameMode, setGameMode,
  currentLine, isEndGame, availableImprovScripts, selectedImprovScript, setSelectedImprovScript,
  setForceRemove, forceRemove,
  game, setGame, guessesMade, activeVote, voteBudgetRemaining, lineVoteFeed,
  setLeaderboard, returnToPartyPack, serverType, isMusicMuted,
  reconnectHostSession, hostConnected, resetEverything,
}) => {

  // Reusable prop bundle to keep things clean
  const commonProps = {
    socket, ipAddress: serverIP, sessionCreated, createSession, gameStarted,
    sessionId, players, rounds, setRounds, currentRound, sessionList,
    leaderboard, removePlayer, titleTheme, AudioPlayer, isEndScene,
    speakingTheme, guessingTheme, gameMode, setGameMode, currentLine,
    isEndGame, availableImprovScripts, selectedImprovScript, setSelectedImprovScript, setForceRemove, forceRemove,
  };

  const handleGameSelect = (gameId) => {
    setGame(gameId);
    sessionStorage.setItem('game', gameId);
    createSession(gameId);
  };

  const handleBackToTitle = () => {
    // If there's an active session, close it first before resetting
    if (sessionId && socket) {
      console.log('Closing session before back to title:', sessionId);
      let callbackFired = false;
      
      socket.emit('closeSession', { sessionId }, (response) => {
        callbackFired = true;
        console.log('Session close response:', response);
        if (typeof resetEverything === 'function') {
          resetEverything();
        }
      });
      
      // Fallback timeout in case callback doesn't fire
      setTimeout(() => {
        if (!callbackFired) {
          console.log('Session close callback timeout - resetting anyway');
          if (typeof resetEverything === 'function') {
            resetEverything();
          }
        }
      }, 2000);
      return;
    }

    // No active session, just reset
    if (typeof resetEverything === 'function') {
      resetEverything();
      return;
    }

    if (socket) {
      socket.disconnect();
    }

    if (typeof returnToPartyPack === 'function') {
      returnToPartyPack();
    }
  };

  const renderActiveGame = () => {
    switch (game) {
      case 'improv':
        return (
          <ImprovHost
            {...commonProps}
            serverIP={serverIP}
            serverType={serverType}
            guessesMade={guessesMade}
            activeVote={activeVote}
            voteBudgetRemaining={voteBudgetRemaining}
            lineVoteFeed={lineVoteFeed}
          />
        );
      case 'guessing-gameshow':
        return <GuessingHost {...commonProps} onReturnToPartyPack={returnToPartyPack} forcedMode="gameshow" serverType={serverType} isMusicMuted={isMusicMuted} />;
      case 'guessing-spiral':
        return <GuessingHost {...commonProps} onReturnToPartyPack={returnToPartyPack} forcedMode="spiral" serverType={serverType} isMusicMuted={isMusicMuted} />;
      case 'trivia':
        return <TriviaHost {...commonProps} setPlayers={setPlayers} setLeaderboard={setLeaderboard} />;
      case 'learn':
        return <LearnHost {...commonProps} setPlayers={setPlayers} setLeaderboard={setLeaderboard} />;
      case 'andscene':
        return <AndSceneHost socket={socket} sessionId={sessionId} players={players} ipAddress={serverIP} serverType={serverType} onReturnToPartyPack={returnToPartyPack} removePlayer={removePlayer} setForceRemove={setForceRemove} isMusicMuted={isMusicMuted} />;
      case 'wordmonster':
        return <WordMonsterHost socket={socket} sessionId={sessionId} players={players} ipAddress={serverIP} serverType={serverType} onReturnToPartyPack={returnToPartyPack} removePlayer={removePlayer} setForceRemove={setForceRemove} isMusicMuted={isMusicMuted} />;
      case 'reaction':
        return <ReactionHost socket={socket} sessionId={sessionId} players={players} ipAddress={serverIP} serverType={serverType} onReturnToPartyPack={returnToPartyPack} removePlayer={removePlayer} setForceRemove={setForceRemove} />;
      case 'setthebar':
        return <SetTheBarHost socket={socket} sessionId={sessionId} players={players} ipAddress={serverIP} serverType={serverType} onReturnToPartyPack={returnToPartyPack} removePlayer={removePlayer} setForceRemove={setForceRemove} isMusicMuted={isMusicMuted} />;
      default:
        return null;
    }
  };

  // Group games dynamically by category for the UI
  const groupedGames = GAME_LIBRARY.reduce((acc, g) => {
    if (!acc[g.category]) acc[g.category] = [];
    acc[g.category].push(g);
    return acc;
  }, {});

  const shouldShowGameSelector = !sessionCreated || !game;

  return (
    <div>
      {shouldShowGameSelector ? (
        <div className="">
          <button className="host-title-button" type="button" onClick={handleBackToTitle}>
            Back to Title
          </button>
          <h1 className="selector-header">Select a Game</h1>

          {Array.isArray(sessionList) && sessionList.length > 0 && (
            <div className="category-section">
              <h3 className="category-title">Reconnect Existing Session</h3>
              <div className="game-grid">
                {sessionList.map((activeSessionId) => (
                  <button
                    key={`reconnect-${activeSessionId}`}
                    className="game-card"
                    style={{
                      '--card-gradient': 'linear-gradient(135deg, #f6d365 0%, #fda085 100%)',
                      '--card-shadow': 'rgba(253, 160, 133, 0.35)'
                    }}
                    onClick={() => reconnectHostSession(activeSessionId, true)}
                  >
                    <div className="game-icon">🔁</div>
                    <h2 className="game-title">Take Over Session {activeSessionId}</h2>
                    <p className="game-desc">Reconnect as host if the original host disconnected.</p>
                  </button>
                ))}
              </div>
            </div>
          )}
          
          {Object.keys(groupedGames).map((category) => (
            <div key={category} className="category-section">
              <h3 className="category-title">{category}</h3>
              <div className="game-grid">
                
                {groupedGames[category].map((g) => (
                  <button
                    key={g.id}
                    className="game-card"
                    style={{
                      '--card-gradient': g.gradient,
                      '--card-shadow': g.shadow
                    }}
                    onClick={() => handleGameSelect(g.id)}
                  >
                    <div className="game-icon">{g.icon}</div>
                    <h2 className="game-title">{g.title}</h2>
                    <p className="game-desc">{g.desc}</p>
                  </button>
                ))}

              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="active-game-container">
          {!hostConnected && (
            <div className="host-connection-warning" style={{ marginBottom: '12px', color: '#b45309', fontWeight: 700 }}>
              Host connection is recovering. Game controls will resume when host reconnects.
            </div>
          )}
          {renderActiveGame()}
        </div>
      )}
    </div>
  );
};

export default Hosts;
